import * as React from "react";
import { useEffect, useMemo } from "react";
import { Button, styled } from "@mui/material";
import { DemoZoomContent } from "./useDemoReasoningController";
import { usePatient } from "../../../state/patientContext";
import { GraphSelectionEdge } from "../../../state/stateManagers/demoPaneStateManager";
import { featuresToMultiLine } from "fe-common/logic";
import { PairGraphEdges, RuleType } from "fe-common/graphql";
import {
  MobileReasoningTopArea,
  MobileReasoningTopTitle,
} from "./mobileSuggestionReasoningBox";

//region [[ Styles ]]

const DemoModeGraphSelectionDetailsView = styled("div")({});

const Edge = styled("div")({
  margin: "5px",
  display: "flex",
  flexFlow: "column",
});

const CitaionTitle = styled("div")({
  fontWeight: 600,
  fontSize: "14px",
  color: "#120078",
});

const CitationText = styled("div")({
  margin: 0,
  marginBottom: "8px",
  display: "flex",
  flexFlow: "column",
  fontSize: "14px",
  color: "#5B5166",
});

const ReadMoreButton = styled(Button)({
  "&.read-more, &.read-more:focus, &.read-more:hover, &.read-more:focus": {
    width: "fit-content",
    background: "transparent",
    margin: 0,
    marginLeft: "-5px",
    padding: "0 5px",
    "& .patient-MuiButton-label": {
      textTransform: "none",
      color: "#060EDF",
      fontSize: "14px",
    },
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface DemoModeGraphSelectionDetailsProps extends DemoZoomContent {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
const ruleToText = (e: PairGraphEdges) => {
  switch (e.type) {
    case RuleType.Isa:
      return "is a type of ";
    case RuleType.Association:
      return "is associated with";
    case RuleType.SourceCauseTarget:
      return "causes";
    case RuleType.TargetCauseSource:
      return "is caused by";
    case RuleType.Lift:
      return "is a factor for";
    case RuleType.Subtype:
      return "is subtype";
  }
};

const edgeToText: (GraphSelectionEdge, noProb?: boolean) => string = (
  edge: GraphSelectionEdge,
  noProb = false,
) => {
  if (!edge) return "";
  const sourceText = featuresToMultiLine(edge.source?.qfs);
  const targetText = featuresToMultiLine(edge.target?.qfs);
  const rule = ruleToText(edge.path);

  return `${sourceText} ${rule} ${targetText} ${noProb ? "" : prob(edge.path)}`;
};

export const prob = (e: PairGraphEdges) => {
  const approx = ""; // TODO e.exactCitation ? "" : "≈";
  // patch - server returns wrong values here
  let t = e.type;
  if (e.lift > 1 && !e.prob) t = RuleType.Lift;
  switch (t) {
    case RuleType.Isa:
      return "";
    case RuleType.Lift:
      return `with lift ${e.lift} x`;
    default:
      return `in ${approx}${formatN(e.prob)}% of cases`;
  }
};

function formatN(n: number) {
  return (n * 100.0).toFixed(1).padStart(3, " ");
}

export const DemoModeGraphSelectionDetails = ({
  setTitle,
  desktop,
  ...props
}: DemoModeGraphSelectionDetailsProps) => {
  const { graphSelection } = usePatient();

  const title = useMemo(() => edgeToText(graphSelection?.edges?.[0]), [
    graphSelection,
  ]);

  useEffect(() => {
    if (desktop) {
      setTitle(title);
    }
  }, [title, graphSelection, setTitle, desktop]);

  return (
    <DemoModeGraphSelectionDetailsView>
      {!desktop && (
        <MobileReasoningTopArea className={"top-area"}>
          <MobileReasoningTopTitle className={"top-title"}>
            {title}
          </MobileReasoningTopTitle>
        </MobileReasoningTopArea>
      )}
      {graphSelection?.edges?.map((rv, index) => (
        <Edge key={index} className={"edge"}>
          {rv?.path?.ruleValues
            ?.filter((r) => r.ruleValue?.citationsObj) // Filter our rules without exact citation
            .map((r) => {
              const citationId = r.ruleValue.citationsObj?.id;
              return (
                <CitationText key={citationId} className={"citation"}>
                  <CitaionTitle className={"citation-title"}>
                    {edgeToText({
                      path: r,
                      source: rv.source,
                      target: rv.target,
                    })}
                  </CitaionTitle>
                  {r.ruleValue?.citationsObj?.vancouver ||
                    "no citation (" + r.ruleValue.ruleValueId + ")"}
                  <ReadMoreButton
                    className={"read-more"}
                    onClick={() =>
                      window.open(r.ruleValue?.citationsObj?.url, "_blank")
                    }
                  >
                    Read More
                  </ReadMoreButton>
                </CitationText>
              );
            })}
        </Edge>
      ))}
    </DemoModeGraphSelectionDetailsView>
  );
};
